import { useEffect, useState } from 'react';
import { collection, limit as fsLimit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Conversation } from '../lib/types';
import { GROUP_BUCKETS, bucketOf, type ViewGroup } from '../lib/inboxViews';

export interface ViewCounts {
  ai: number;
  waiting: number;
  progress: number;
  /** Parked on the customer / on another team (both are snoozed chats). */
  wait_customer: number;
  wait_internal: number;
  /** Active chats whose last AI answer was flagged 'bad' (orthogonal bucket). */
  flagged: number;
  /** Active total = ai + waiting + progress (snoozed/resolved excluded). */
  all: number;
  /** The simple bar's grouped tabs. Summed from the members above rather than
   *  counted again, so a group badge can't disagree with the tabs it collapses
   *  (see GROUP_BUCKETS). */
  needs_you: number;
  waiting_any: number;
}

const EMPTY: ViewCounts = {
  ai: 0,
  waiting: 0,
  progress: 0,
  wait_customer: 0,
  wait_internal: 0,
  flagged: 0,
  all: 0,
  needs_you: 0,
  waiting_any: 0,
};

/** A grouped tab's badge = the sum of the members it collapses, read off
 *  GROUP_BUCKETS. 'resolved' is in no group and the listener excludes it
 *  anyway, so it contributes nothing. */
function sumGroup(counts: ViewCounts, group: ViewGroup): number {
  return GROUP_BUCKETS[group].reduce((n, b) => n + (b === 'resolved' ? 0 : counts[b]), 0);
}

/**
 * Live counts for the New / Open / In-progress tabs. Listens to active chats
 * (open/pending/snoozed) and buckets by the same rule the list uses. Resolved
 * is excluded (no badge needed, keeps the read bounded). Inbox-scope filter
 * applied client-side, mirroring useUnreadByStatus.
 */
export function useInboxViewCounts(inboxIds?: string[]): ViewCounts {
  const [counts, setCounts] = useState<ViewCounts>(EMPTY);
  const inboxKey = (inboxIds ?? []).slice().sort().join(',');

  useEffect(() => {
    const q = query(
      collection(db, 'chats'),
      where('status', 'in', ['open', 'pending', 'snoozed']),
      fsLimit(500),
    );
    const allowed = inboxIds && inboxIds.length > 0 ? new Set(inboxIds) : null;
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next: ViewCounts = { ...EMPTY };
        for (const d of snap.docs) {
          const data = d.data() as Pick<Conversation, 'status' | 'assigneeUid' | 'aiHandled' | 'waitReason' | 'lastAiDecision'> & { inboxId?: string; lastMessageAt?: number };
          // When scoped to inboxes, a chat with no inboxId is out of scope
          // (mirrors useChats' server-side inboxId 'in' filter).
          if (allowed && (!data.inboxId || !allowed.has(data.inboxId))) continue;
          // Deferred "ghost" chats (widget opened, customer never spoke) are
          // created WITHOUT lastMessageAt (createChat deferListing) so they stay
          // out of the inbox list — orderBy('lastMessageAt') omits docs missing
          // the field. Skip them here too, else the tab badge shows a phantom
          // count with an empty list.
          if (!data.lastMessageAt) continue;
          const b = bucketOf(data);
          if (b === 'ai' || b === 'waiting' || b === 'progress') {
            next[b] += 1;
            next.all += 1;
          }
          // Parked chats are snoozed, so they stay out of the "active total"
          // (same rule as any other snoozed chat) but still need their own badge.
          if (b === 'wait_customer' || b === 'wait_internal') next[b] += 1;
          // Flagged is orthogonal — a chat can be in any handling bucket AND have
          // a 'bad'-rated last AI answer.
          if (data.lastAiDecision?.feedback?.verdict === 'bad') next.flagged += 1;
        }
        next.needs_you = sumGroup(next, 'needs_you');
        next.waiting_any = sumGroup(next, 'waiting_any');
        setCounts(next);
      },
      (err) => console.error('useInboxViewCounts listener error', err),
    );
    return unsub;
  }, [inboxKey]);

  return counts;
}
